import axios, { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useAddRoleMaster, useDeleteRoleMaster, useRoleMaster } from "./queries";
import { useMenuMaster } from "../LegacyMenuMasterConfig/queries";
import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FaPlusCircle, FaSearch } from "react-icons/fa";
import { Button as RBButton, Col, Form, Row } from "react-bootstrap";
import Select from 'react-select';
import { RoleMaster, RoleMasterForm } from "../../models/config/RoleMaster";
import { useCompanyMaster } from "../../queries";
import { CompanyMaster } from "models/companymaster/CompanyMaster";
import { useBoolean } from "usehooks-ts";
import { extractAccessToken } from "helpers/jwt";
import { RoleMasterFormModal, RoleMasterTable } from "./components";
import { useUserInformation } from "hooks";

const selectSmStyles = {
    control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
    valueContainer: (base) => ({ ...base, height: '31px', padding: '0 6px' }),
    indicatorsContainer: (base) => ({ ...base, height: '31px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const LegacyRoleMasterConfig = () => {
    const queryClient = useQueryClient();

    const { data: roleMaster } = useRoleMaster();

    const { data: menuMaster } = useMenuMaster();
    const menuMasterMap = useMemo(
        () =>
            menuMaster
                ? new Map(menuMaster.map(menu => [menu.id, menu]))
                : undefined,
        [menuMaster]
    );

    const roleMasterForms = useMemo(() => {
        if (!menuMasterMap || !roleMaster) {
            return undefined;
        }

        const output = new Map<string, RoleMasterForm>();
        roleMaster.forEach(role => {
            const menu = menuMasterMap.get(role.menu_id);
            if (!menu) {
                return;
            }

            const roleMasterForm = output.get(role.sid + role.id);
            if (!roleMasterForm) {
                output.set(role.sid + role.id, {
                    sid: role.sid,
                    id: role.id,
                    roleDetail: role.role_detail,
                    allowMenus: [menu]
                });
            } else {
                roleMasterForm.allowMenus.push(menu);
            }
        });

        return Array.from(output.values());
    }, [menuMasterMap, roleMaster]);

    const { companyOptions } = useUserInformation();
    const { data: companyMaster } = useCompanyMaster();

    const [pendingCompany, setPendingCompany] = useState<CompanyMaster>();
    const [pendingSearchRoleId, setPendingSearchRoleId] = useState('');
    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();
    const [searchRoleId, setSearchRoleId] = useState('');

    const handleSearch = () => {
        setSelectedCompany(pendingCompany);
        setSearchRoleId(pendingSearchRoleId);
    };

    const filteredMasterRoleForms = useMemo(
        () =>
            roleMasterForms?.filter(roleMasterForm => {
                const isMatchedSID =
                    selectedCompany && selectedCompany.sid !== 'all'
                        ? roleMasterForm.sid === selectedCompany.sid
                        : true;
                const isMatchedRoleId = roleMasterForm.id
                    .toLowerCase()
                    .includes(searchRoleId.toLowerCase());

                return isMatchedSID && isMatchedRoleId;
            }),
        [companyMaster, roleMasterForms, selectedCompany, searchRoleId]
    );

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();

    const [selectedEditRoleMasterForm, setSelectedEditRoleMasterForm] =
        useState<RoleMasterForm>();
    const isOpenEditModal = !!selectedEditRoleMasterForm;

    const { mutate: addRoleMaster, mutateAsync: addRoleMasterAsync } =
        useAddRoleMaster();
    const { mutate: deleteRoleMaster, mutateAsync: deleteRoleMasterAsync } =
        useDeleteRoleMaster();

    const handleSubmitAddRoleMaster = (formValues: RoleMasterForm) => {
        const payload = extractAccessToken();

        const createdBy = payload.citizen_id || payload.username;
        const requests = formValues.allowMenus.map(
            menu =>
                ({
                    sid: formValues.sid,
                    id: formValues.id,
                    role_detail: formValues.roleDetail,
                    menu_id: menu.id,
                    created_by: createdBy
                }) as RoleMaster
        );

        requests.forEach(request =>
            addRoleMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    toggleIsOpenCreateModal();

                    await queryClient.invalidateQueries({
                        queryKey: ['role-master']
                    });
                }
            })
        );
    };

    const handleEditRoleMasterForm = (roleMasterForm: RoleMasterForm) => {
        setSelectedEditRoleMasterForm(roleMasterForm);
    };

    const handleCloseEditModal = () => {
        setSelectedEditRoleMasterForm(undefined);
    };

    const handleSubmitEditRoleMaster = async (formValues: RoleMasterForm) => {
        const currentMasterRoleForm = roleMasterForms?.find(
            roleMasterForm => roleMasterForm.id === formValues.id
        );
        if (!currentMasterRoleForm) {
            return;
        }

        const currentAllowedMenus = currentMasterRoleForm.allowMenus;

        const newAllowedMenuIdSet = new Set(
            formValues.allowMenus.map(menu => menu.id)
        );
        const removeRoleMasterRequests = currentAllowedMenus
            .filter(menu => !newAllowedMenuIdSet.has(menu.id))
            .map(
                menu =>
                    ({
                        sid: formValues.sid,
                        id: formValues.id,
                        menu_id: menu.id
                    }) as RoleMaster
            );

        const payload = extractAccessToken();
        const currentAllowedMenuIdSet = new Set(
            currentAllowedMenus.map(menu => menu.id)
        );
        const addRoleMasterRequests = formValues.allowMenus
            .filter(menu => !currentAllowedMenuIdSet.has(menu.id))
            .map(
                menu =>
                    ({
                        sid: formValues.sid,
                        id: formValues.id,
                        menu_id: menu.id,
                        role_detail: formValues.roleDetail,
                        created_by: payload.citizen_id || payload.username
                    }) as RoleMaster
            );

        try {
            await Promise.all([
                ...removeRoleMasterRequests.map(request =>
                    deleteRoleMasterAsync(request)
                ),
                ...addRoleMasterRequests.map(request =>
                    addRoleMasterAsync(request)
                )
            ]);

            handleCloseEditModal();

            await Swal.fire({ title: 'Success!', icon: 'success' });
            await queryClient.invalidateQueries({ queryKey: ['role-master'] });
        } catch (error) {
            await Swal.fire({
                title: 'Error!',
                icon: 'error',
                text: getErrorMessage(error)
            });
        }
    };

    const handleDeleteRoleMaster = async (formValues: RoleMasterForm) => {
        const result = await Swal.fire({
            title: 'ลบข้อมูล ' + formValues.id + ' ?',
            text: 'คุณต้องการลบหรือไม่ ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: '✔ ใช่',
            cancelButtonText: '✖ ไม่',
            allowOutsideClick: false
        });
        if (!result.isConfirmed) {
            return;
        }

        const requests = formValues.allowMenus.map(
            menu =>
                ({
                    sid: formValues.sid,
                    id: formValues.id,
                    menu_id: menu.id
                }) as RoleMaster
        );

        requests.forEach(request =>
            deleteRoleMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({ title: 'Success!', icon: 'success' });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: ['role-master']
                    });
                }
            })
        );
    };

    return (
        <div className="master-data-content">
            <div>
                <div className="trapezoid-head" style={{ width: 'auto', display: 'inline-block' }}>
                    <span>Workflow Role Master Config</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <RBButton variant="info" size="sm" style={{ float: 'right', marginLeft: '5px' }} onClick={toggleIsOpenCreateModal}>
                        <FaPlusCircle style={{ marginBottom: '4px' }} /> Add
                    </RBButton>
                </div>
            </div>

            <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                <div className='trapezoid trapezoid-purple' style={{ width: '200px', display: 'inline-block' }}>
                    <span>Search menu</span>
                </div>
                <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                    <Row>
                        <Col sm={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Company :</Form.Label>
                                <Select
                                    menuPortalTarget={document.body}
                                    styles={selectSmStyles}
                                    options={companyOptions}
                                    value={pendingCompany}
                                    getOptionLabel={(company: CompanyMaster) => company.company_name}
                                    getOptionValue={(company: CompanyMaster) => company.sid}
                                    onChange={setPendingCompany}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Role ID :</Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={pendingSearchRoleId}
                                    onChange={e => setPendingSearchRoleId(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={{ span: 2, offset: 2 }}>
                            <div className="mb-3 w-100 mt-4 d-flex justify-content-end">
                                <RBButton variant="info" size="sm" className="mx-1 btn btn-block" onClick={handleSearch}>
                                    <FaSearch /> Search
                                </RBButton>
                            </div>
                        </Col>
                    </Row>
                </div>

                <RoleMasterTable
                    roleMasterForms={filteredMasterRoleForms}
                    onEdit={handleEditRoleMasterForm}
                    onDelete={handleDeleteRoleMaster}
                />

                <RoleMasterFormModal
                    title="Create Role"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddRoleMaster}
                />

                <RoleMasterFormModal
                    disabledKeyField
                    title="Edit Role"
                    defaultValues={selectedEditRoleMasterForm}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditRoleMaster}
                />
            </div>
        </div>
    );
}
